import type React from 'react';
import type {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import {
  Alert,
  AlertDescription,
  FormSelect,
  Input,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import { canSelectNewAdmissionForSoc } from '../../pages/CreatePatientForm/patientMatchUtils';
import { getVisitTypePatch, type SchedulingEmr } from './schedulingVisitType';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';

/**
 * Visit type / creation type / patient type row shown above the scheduling body for
 * an existing patient. The dropdown options and saved field names vary per EMR.
 */
export function VisitCreationRow({
  emr,
  register,
  setValue,
  errors,
  visitType,
  creationType,
  hasMatchedEpisodeDischargeDate,
  socBlockedByOpenAdmission,
  rocBlockedByNoActiveEpisode,
  handleFieldBlur,
  saveToServer,
  isWrmClinic,
}: {
  emr: SchedulingEmr;
  register: UseFormRegister<SchedulingStepFormData>;
  setValue: UseFormSetValue<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  // 'rec' never renders here (REC is new-patient-only; this row is existing-patient),
  // but the shared watch value includes it.
  visitType: 'soc' | 'roc' | 'rec' | null | undefined;
  creationType:
    'new_admission' | 'new_episode' | 'current_episode' | null | undefined;
  hasMatchedEpisodeDischargeDate: boolean;
  socBlockedByOpenAdmission: boolean;
  rocBlockedByNoActiveEpisode: boolean;
  handleFieldBlur: (e: React.FocusEvent<HTMLElement>) => void;
  saveToServer: (data: Partial<SchedulingStepFormData>) => Promise<void> | void;
  isWrmClinic: boolean;
}) {
  // Visit type / creation type are stored on shared keys across EMRs.
  const visitField = 'kinnser_visit_type' as const;
  const creationField = 'kinnser_creation_type' as const;

  // SOC stays selectable even when the matched patient has an open admission —
  // the block surfaces at the creation-type step (neither EMR creates a new
  // admission until the prior one is discharged/inactivated), where
  // "New admission" is disabled and the alert below explains why. The wording
  // names where the user fixes it: Kinnser, or the Roger patient chart.
  const openAdmissionReason =
    emr === 'roger'
      ? 'This patient has an open admission. Add the next episode from the patient chart, or discharge the admission first.'
      : 'To create a new admission, you must first inactivate or discharge the latest admission in Kinnser.';
  const newAdmissionDisabledReason =
    emr === 'roger'
      ? 'Not available while an admission is open.'
      : 'Cannot select because admission is active or has not been discharged.';
  // ROC resumes a current episode; with no active episode there is nothing to resume.
  const rocUnavailableReason =
    'ROC is unavailable because this patient has no active episode to resume.';
  const visitTypeOptions = [
    { value: 'soc' as const, label: 'SOC' },
    {
      value: 'roc' as const,
      label: 'ROC',
      disabled: rocBlockedByNoActiveEpisode,
      disabledReason: rocBlockedByNoActiveEpisode
        ? rocUnavailableReason
        : undefined,
    },
  ];

  // Only warn about the open admission once the user is actually trying to
  // create a SOC — ROC and the unselected state shouldn't surface it.
  const showOpenAdmissionAlert =
    socBlockedByOpenAdmission && visitType === 'soc';

  // "New admission" is allowed when no open admission blocks SOC and either the matched
  // episode is discharged or the patient has no active episode at all — see
  // canSelectNewAdmissionForSoc.
  const canSelectNewAdmission = canSelectNewAdmissionForSoc(
    hasMatchedEpisodeDischargeDate,
    socBlockedByOpenAdmission,
    rocBlockedByNoActiveEpisode
  );

  // SOC for Kinnser (WRM and non-WRM): only "New admission" — same as non-WRM Kinnser scheduling.
  const creationOptions =
    visitType === 'soc'
      ? [
          {
            value: 'new_admission' as const,
            label: 'New admission',
            disabled: !canSelectNewAdmission,
            disabledReason: newAdmissionDisabledReason,
          },
        ]
      : visitType === 'roc'
        ? [{ value: 'current_episode' as const, label: 'Current episode' }]
        : [];

  const visitTypeHint =
    isWrmClinic && visitType === 'roc'
      ? 'Pushing not available for ROC.'
      : isWrmClinic && visitType === 'soc' && creationType === 'new_admission'
        ? 'Creates admission + episode without a visit'
        : null;

  return (
    <div className='mb-4 pb-4 border-b border-gray-200 flex flex-col gap-3'>
      {showOpenAdmissionAlert && (
        <Alert className='py-2 px-3 bg-amber-50 border-amber-200'>
          <div className='flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4 text-amber-600' />
            <AlertDescription className='text-amber-800 text-xs'>
              {openAdmissionReason}
            </AlertDescription>
          </div>
        </Alert>
      )}
      <div className='flex items-center gap-6 flex-wrap'>
        <div className='flex flex-col gap-1'>
          <FormSelect
            label='Visit type'
            placeholder='Select visit type...'
            options={visitTypeOptions}
            error={errors[visitField]?.message}
            {...register(visitField, {
              onBlur: handleFieldBlur,
              onChange: async e => {
                const v = e.target.value;
                if (v !== 'soc' && v !== 'roc') return;
                const patch = getVisitTypePatch(emr, v, canSelectNewAdmission);
                (
                  Object.keys(patch) as Array<keyof SchedulingStepFormData>
                ).forEach(key => {
                  setValue(
                    key,
                    patch[
                      key
                    ] as SchedulingStepFormData[keyof SchedulingStepFormData],
                    { shouldDirty: true }
                  );
                });
                await saveToServer(patch);
              },
            })}
          />
          {visitTypeHint && (
            <p className='text-xs text-gray-700'>{visitTypeHint}</p>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <FormSelect
            label='Creation type'
            placeholder='Select creation type...'
            options={creationOptions}
            error={errors[creationField]?.message}
            {...register(creationField, {
              onBlur: handleFieldBlur,
              onChange: async e => {
                let v:
                  'new_admission' | 'new_episode' | 'current_episode' | null =
                  e.target.value === 'new_admission' ||
                  e.target.value === 'new_episode' ||
                  e.target.value === 'current_episode'
                    ? e.target.value
                    : null;
                if (v === 'new_admission' && !canSelectNewAdmission) {
                  v = null;
                }
                setValue(creationField, v, { shouldDirty: true });
                if (v) await saveToServer({ [creationField]: v });
              },
            })}
          />
        </div>
        <div className='flex flex-col gap-1.5'>
          <label className='text-sm font-medium text-gray-700'>
            Patient type
          </label>
          <Input
            value='Existing'
            disabled
            readOnly
            inputSize='default'
            className='w-32 bg-gray-50'
          />
        </div>
      </div>
    </div>
  );
}
